import { minutes } from "../calendar/utils/types";

export const CRON_FIELDS = ['minutes', 'hours', 'daysOfMonth', 'months', 'daysOfWeek', 'weeksOfMonth'];
export const MAX_VALUES = { months: 12, daysOfWeek: 7, weeksOfMonth: 4};

function parseIntList(str: string, offset = 0): number[] {
  if(str === '*') return []
  const ints = str.split(",").map(i => parseInt(i) + offset);
  return [... new Set(ints)]
}

function parseDaysOfWeekStr(str: string) {
  if(str === "*") return {
    daysOfWeek: [], 
    weeksOfMonth: [] as number[]
  };

  // Note: Assume one one day of the week, but possible all/some weeks of month
  // Allow: 1#2,1#3
  // Disallow: 1#2,3#4
  if(str.includes("#")) {
    const weeksOfMonth = str.split(",").map(day => day.split("#")[1]).map(i => parseInt(i));
    const daysOfWeek = [str.split(",").map(day => day.split("#")[0]).map(i => parseInt(i))[0]];
    return { daysOfWeek, weeksOfMonth }
  }

  return {
    daysOfWeek: str.split(",").map(i => parseInt(i)),
    weeksOfMonth: [] as number[]
  }
}


export function parseCron(cron: string, duration: minutes) {
  const [ minutesStr, hoursStr, daysOfMonthStr, monthsStr, daysOfWeekStr, timezone ] = cron.split(" ");
  const [ minutes, hours ] = [ minutesStr, hoursStr ].map(str => parseIntList(str))

  // Note: Cron is months:1-12, but JS Date is months:0-11
  const months = parseIntList(monthsStr, -1)

  const daysOfMonth = parseIntList(daysOfMonthStr)
  const { daysOfWeek, weeksOfMonth } = parseDaysOfWeekStr(daysOfWeekStr)

  return { minutes, hours, daysOfMonth, months, daysOfWeek, weeksOfMonth, timezone }
}

export type ParsedCronData = ReturnType<typeof parseCron>;

export function determineCronType(cronData: ParsedCronData, duration: minutes) {
  const { months, daysOfWeek, weeksOfMonth, daysOfMonth } = cronData;
  if(
    months.length > 0 || 
    daysOfMonth.length > 0 || 
    (duration >= 1440 && daysOfWeek.length <= 1 && weeksOfMonth.length <= 1)
  ) { 
    return "monthly"
  } else if (
    daysOfWeek.length === 1  
  ) { 
    return "weekly"
  } else if (
    weeksOfMonth.length === 0
  ) { 
    return "daily"
  }
}

const adjustDateToDayOfWeek = (date: Date, dayOfWeek: number): void => {
  const diff = dayOfWeek - date.getDay();
  date.setDate(date.getDate() + diff);
};

const adjustDateToWeekOfMonth = (date: Date, weekOfMonth: number): void => {
  const diff = weekOfMonth - Math.floor(date.getDate() / 7);
  date.setDate(date.getDate() + diff * 7);
};

export function fromCron(cron: string, duration: minutes, baseDate: Date = new Date()) {
  const parsedData = parseCron(cron, duration)
  const { minutes, hours, daysOfMonth, months, daysOfWeek, weeksOfMonth, timezone } = parsedData
  const cronType = determineCronType(parsedData, duration)

  const date = new Date(new Date(baseDate).setHours(hours[0], minutes[0], 0, 0))

  if(months.length === 1) { date.setMonth(months[0]) }
  if(daysOfMonth.length === 1) { date.setDate(daysOfMonth[0])}
  if(daysOfWeek.length === 1) { adjustDateToDayOfWeek(date, daysOfWeek[0]) }
  if(months.length === 1 && weeksOfMonth.length === 1) { adjustDateToWeekOfMonth(date, weeksOfMonth[0]) }

  return { minutes, hours, daysOfMonth, months, daysOfWeek, weeksOfMonth, date, cronType, timezone }
}

export type CronData = ReturnType<typeof fromCron>;


export function toCron(data: CronData) {
  const { minutes, hours, daysOfMonth, months, daysOfWeek, weeksOfMonth } = data;
  const minutesStr = minutes.length ? minutes.join(",") : "*";
  const hoursStr = hours.length ? hours.join(",") : "*";
  const daysOfMonthStr = daysOfMonth.length ? daysOfMonth.join(",") : "*";
  const monthsStr = months.length ? months.map(m => m + 1).join(",") : "*"; // JS Date is 0-11
  const daysOfWeekStr = (daysOfWeek.length || weeksOfMonth.length) ? 
    daysOfWeek.flatMap((d) => {
      return weeksOfMonth.length ? weeksOfMonth.map(w => `${d}#${w}`) : `${d}`;
    }).join(",") : "*";

  return `${minutesStr} ${hoursStr} ${daysOfMonthStr} ${monthsStr} ${daysOfWeekStr} ${data.timezone}`;
}

const mergeValues = (oldValues: number[], addValues: number[] = [], subValues: number[] = []): number[] => {
  const merged = [...oldValues, ...addValues];
  const filtered = merged.filter((v) => !subValues.includes(v));
  return [...new Set(filtered)];
};

const applyMaxValues = (key: string, values: number[]): number[] => {
  if(MAX_VALUES[key] && values.length >= MAX_VALUES[key]) {
    return []
  } else {
    return values;
  }
}

export function mergeCronData(
  data: CronData, { add, sub }: {
    add?: Partial<CronData>,
    sub?: Partial<CronData>,
  } = {}) : CronData {
    const updatedCronData = {} as CronData;
    CRON_FIELDS.forEach((key) => {
        const deduped = applyMaxValues(key, mergeValues(data[key], add?.[key], sub?.[key]))
        updatedCronData[key] = deduped
    })
    return {...data, ...updatedCronData};
}